/**
 * Multi-service health hooks.
 *
 * Surfaces /readyz and /metrics from the proxy + control-plane on the
 * Runtime Health and Proxy pages. nginx forwards /proxy/* to the proxy
 * service so this stays same-origin (no CORS).
 */
import { useQuery } from '@tanstack/react-query';

import { apiFetch } from './client.js';

// Same-origin under /proxy (nginx strips the prefix and forwards to the
// proxy service). Override with VITE_PROXY_URL only if running outside
// the gateway box.
const PROXY_BASE = (import.meta.env?.VITE_PROXY_URL as string | undefined) ?? '/proxy';

export interface ReadyzResponse {
  status: string;
  service: string;
  checks: Record<string, string>;
}

export interface HealthzResponse {
  status: string;
  service: string;
  ts: string;
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error(`${url} → ${res.status}`);
  return (await res.json()) as T;
}

async function fetchText(url: string): Promise<string> {
  const res = await fetch(url, { headers: { accept: 'text/plain' } });
  if (!res.ok) throw new Error(`${url} → ${res.status}`);
  return await res.text();
}

export function useControlPlaneReadyz() {
  return useQuery({
    queryKey: ['readyz', 'control-plane'],
    queryFn: () => apiFetch<ReadyzResponse>('/readyz'),
    refetchInterval: 5_000,
  });
}

export function useProxyReadyz() {
  return useQuery({
    queryKey: ['readyz', 'proxy'],
    queryFn: () => fetchJson<ReadyzResponse>(`${PROXY_BASE}/readyz`),
    refetchInterval: 5_000,
  });
}

export function useControlPlaneMetrics() {
  return useQuery({
    queryKey: ['metrics', 'control-plane'],
    // /metrics is text/plain (Prometheus exposition); apiFetch parses JSON,
    // so call fetchText directly. Same-origin via nginx.
    queryFn: () =>
      fetchText(`${(import.meta.env?.VITE_CONTROL_PLANE_URL as string | undefined) ?? ''}/metrics`),
    refetchInterval: 10_000,
  });
}

export function useProxyMetrics() {
  return useQuery({
    queryKey: ['metrics', 'proxy'],
    queryFn: () => fetchText(`${PROXY_BASE}/metrics`),
    refetchInterval: 10_000,
  });
}

/** Lightweight Prometheus text parser — extracts `name{labels} value` rows. */
export interface ParsedMetricLine {
  name: string;
  labels: Record<string, string>;
  value: number;
}

export function parsePrometheusText(raw: string): ParsedMetricLine[] {
  const out: ParsedMetricLine[] = [];
  for (const line of raw.split('\n')) {
    if (!line || line.startsWith('#')) continue;
    const m = line.match(/^([a-zA-Z_:][a-zA-Z0-9_:]*)(\{([^}]*)\})?\s+([0-9eE+\-.]+)$/);
    if (!m) continue;
    const name = m[1]!;
    const labelStr = m[3] ?? '';
    const value = Number(m[4]);
    const labels: Record<string, string> = {};
    if (labelStr) {
      for (const pair of labelStr.split(',')) {
        const [k, v] = pair.split('=');
        if (!k) continue;
        labels[k.trim()] = (v ?? '').trim().replace(/^"|"$/g, '');
      }
    }
    out.push({ name, labels, value });
  }
  return out;
}
